import React, { useMemo } from "react";
import { useNavigate } from "@remix-run/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@core/components/ui/dropdown-menu";
import { cn } from "@core/lib/utils";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { ButtonVariant, Button } from "@core/components/ui/button";
import { useAuthActions } from "@core/hooks/useAuthActions";
import { useTranslation } from "react-i18next";
import Spinner from "@core/components/ui/Spinner";
import { LoginModal } from "@core/components/forms/LoginModal";
import { LogoutOption } from "@phoenix/site/components/MiniMenu/LogoutOption";
import { useRootLoaderData } from "@/root";
import { useTheme } from "@core/hooks/useTheme";
import type { ThemeSettings } from "@/apidtos";
import { ReplaceIfExtension } from "@core/components/PipelineComponents/ReplaceIfExtension";
import { IslandPreloadContext } from "@core/utils/island-preload-registry";
import { HeaderAccountKindPill } from "@/overrides/site/components/HeaderAccountKindPill";
import { useApi } from "@core/api";
import { useQuery } from "@tanstack/react-query";

const AccountIconSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8" r="5" stroke="#ffffff" strokeWidth="1.5" />
    <path
      d="M3 21C3 16.5817 7.02944 13 12 13C16.9706 13 21 16.5817 21 21"
      stroke="#ffffff"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/** Primary account / ordering links (order matches prod mini menu design). */
const MINI_MENU_MAIN: readonly { readonly to: string; readonly labelKey: string; readonly labelDefault: string }[] =
  [
    { to: "/dashboard/settings/profile", labelKey: "ui.account", labelDefault: "Account" },
    { to: "/dashboard/patients", labelKey: "ui.patients", labelDefault: "Patients" },
    { to: "/dashboard/orders", labelKey: "ui.pastOrders", labelDefault: "Past Orders" },
    { to: "/dashboard/settings/addressbook", labelKey: "ui.addresses", labelDefault: "Addresses" },
    { to: "/dashboard/wallet", labelKey: "ui.payments", labelDefault: "Payments" },
  ] as const;

const MINI_MENU_SUPPORT: readonly { readonly to: string; readonly labelKey: string; readonly labelDefault: string }[] =
  [
    { to: "/support", labelKey: "ui.supportCenter", labelDefault: "Support Center" },
    { to: "/feedback", labelKey: "ui.sendFeedback", labelDefault: "Send Feedback" },
  ] as const;

function resolveMiniMenuButtonVariant(
  theme: ThemeSettings | undefined,
  propVariant: ButtonVariant | undefined
): ButtonVariant {
  if (propVariant) {
    return propVariant;
  }
  const v = theme?.Sections?.Header?.MiniMenuButton?.Variant;
  const fromBreakpoint = v?.BreakpointValues?.xs?.Variant;
  const fromFlat = (v as { Value?: string } | undefined)?.Value;
  const resolved = (fromBreakpoint ?? fromFlat) as ButtonVariant | undefined;
  return resolved ?? "primary";
}

interface MiniMenuProps {
  loginLabel?: string;
  signOutBtnClassName?: string;
  variant?: ButtonVariant;
  skipSubmit?: boolean;
}

export const MiniMenu = ({
  loginLabel,
  signOutBtnClassName,
  variant,
  skipSubmit
}: MiniMenuProps): JSX.Element => {
  const { theme } = useRootLoaderData();
  const { user, isLoading } = useCurrentUser();
  const { logout } = useAuthActions();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useApi();
  const providerContextQuery = useQuery({
    queryKey: ["providerContext"],
    queryFn: () => api.GetProviderContext(),
    staleTime: 60_000,
    enabled: !!user?.Id,
  });
  const isPrescriber = (providerContextQuery.data?.Providers ?? []).some(
    p => p.LinkType !== "Staff",
  );
  const c = useTheme("Header");
  const miniMenuButtonTheme = c(
    "MiniMenuButton",
    "font-normal shadow-none border border-neutral-200/80 cursor-pointer"
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const allowNextOpenRef = React.useRef(false);
  const closeMenu = React.useCallback(() => {
    allowNextOpenRef.current = false;
    setIsOpen(false);
    if (typeof document !== "undefined") {
      const activeElement = document.activeElement as HTMLElement | null;
      activeElement?.blur();
    }
  }, []);

  const appliedVariant = useMemo(
    () => resolveMiniMenuButtonVariant(theme as ThemeSettings | undefined, variant),
    [variant, theme]
  );

  if (isLoading) {
    return (
      <Button {...miniMenuButtonTheme} variant={appliedVariant}>
        <Spinner />
      </Button>
    );
  }

  if (!user) {
    return (
      <LoginModal asChild skipSubmit={skipSubmit}>
        <button
          type="button"
          className="header-icon-btn"
          data-testid="header-signin-btn"
          aria-label={loginLabel || t("ui.signIn", "Sign In")}
          title={loginLabel || t("ui.signIn", "Sign In")}>
          <AccountIconSvg />
          <span className="sr-only">{loginLabel || t("ui.signIn", "Sign In")}</span>
        </button>
      </LoginModal>
    );
  }

  return (
    <div className="account-dropdown hidden items-center gap-2.5 md:flex" data-testid="header-account-dropdown">
      <HeaderAccountKindPill />
      <DropdownMenu
        open={isOpen}
        onOpenChange={(nextOpen) => {
          if (nextOpen && !allowNextOpenRef.current) {
            return;
          }
          allowNextOpenRef.current = false;
          setIsOpen(nextOpen);
        }}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="header-icon-btn"
            data-testid="header-account-btn"
            aria-haspopup="true"
            aria-expanded={isOpen}
            onPointerDown={() => {
              allowNextOpenRef.current = true;
            }}
            onKeyDown={() => {
              allowNextOpenRef.current = true;
            }}>
            <ReplaceIfExtension name="site.miniMenu.buttonContent">
              <AccountIconSvg />
            </ReplaceIfExtension>
            <span className="sr-only">{user.Contact?.FirstName ?? user.UserName}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={cn(
            // Match header dropdown panel: stacked links, separators, scroll on small viewports.
            "flex max-h-[70vh] min-w-[220px] flex-col overflow-y-auto py-2"
          )}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
          }}>
          {MINI_MENU_MAIN.map((link, index) => {
            const label = t(link.labelKey, link.labelDefault);
            const isAccountRow = index === 0;
            return (
              <DropdownMenuItem
                key={link.to}
                data-testid={`header-account-link-${link.to.replace(/^\//, "").replace(/\//g, "-")}`}
                className={
                  isAccountRow
                    ? cn(
                        // Persistent “Account” band (gold on warm brown) matching reference header menu.
                        "rounded-none px-5 py-2.5",
                        "bg-[rgba(120,82,42,0.55)] text-[#EFBD45]",
                        "hover:bg-[rgba(140,96,52,0.65)] hover:text-[#F5D078] focus:bg-[rgba(140,96,52,0.65)] focus:text-[#F5D078]"
                      )
                    : "rounded-none"
                }
                onSelect={(event) => {
                  event.preventDefault();
                  closeMenu();
                  navigate(link.to);
                }}>
                {label}
              </DropdownMenuItem>
            );
          })}

          {isPrescriber && (
            <DropdownMenuItem
              data-testid="header-account-link-dashboard-pending-orders"
              className="rounded-none"
              onSelect={(event) => {
                event.preventDefault();
                closeMenu();
                navigate("/dashboard/pending-orders");
              }}>
              {t("ui.pendingOrders.title", "Pending Orders")}
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator className="my-2" />

          {MINI_MENU_SUPPORT.map((link) => (
            <DropdownMenuItem
              key={link.to}
              data-testid={`header-account-link-${link.to.replace(/^\//, "").replace(/\//g, "-")}`}
              className="rounded-none"
              onSelect={(event) => {
                event.preventDefault();
                closeMenu();
                navigate(link.to);
              }}>
              {t(link.labelKey, link.labelDefault)}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator className="my-2" />

          <DropdownMenuItem
            className="rounded-none p-0 focus:bg-transparent"
            data-testid="header-signout-item"
            onSelect={(event) => {
              event.preventDefault();
            }}>
            <LogoutOption
              signOutBtnClassName={cn(
                "justify-start px-5 py-2.5 text-left text-[13px] font-normal leading-[1.4] text-zinc-400",
                "w-full hover:bg-[rgba(239,189,69,0.12)] hover:text-zinc-200 focus-visible:outline-none",
                signOutBtnClassName
              )}
              signOutLabel={t("ui.signOut", "Sign Out")}
              onLogout={() =>
                logout.mutate({
                  onSuccessCallback: () => {
                    navigate("/");
                  },
                })
              }
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default MiniMenu;

export const islandPreload = async (
  _ctx: IslandPreloadContext,
  _props?: Record<string, unknown>
) => {
  return {};
};
